import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  useAddTimelineCommentMutation,
  useAddTimelineReactionMutation,
  useRemoveTimelineReactionMutation,
} from "../../../../app/communicationApiSlice";
import { useUploadFileMutation } from "../../../../app/utilsApiSlice";
import UserAvatar from "../../../common/UserAvatar";
import PostMediaGallery from "./PostMediaGallery";
import toast from "../../../../helpers/toast";
import { selectCurrentUser } from "../../auth/authSlice";

const QUICK_EMOJIS = ["👍", "❤️", "👏", "🔥", "😂", "😮", "🎉", "✅", "💯", "🙏", "👀", "💪"];

function timeAgo(value) {
  if (!value) return "";
  const diff = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function audienceLabel(post) {
  if (post.classroom) return `Class: ${post.classroom.name}`;
  if (post.audience_type === "teachers") return "All teachers";
  return "All parents";
}

function CommentItem({ comment }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <UserAvatar
        src={comment.author?.photo}
        name={comment.author?.name || "?"}
        sx={{ width: 28, height: 28, fontSize: "0.7rem", flexShrink: 0, mt: 0.25 }}
      />
      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="caption" fontWeight={700} display="block">
          {comment.author?.name || "Unknown"}
        </Typography>
        {comment.body && (
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {comment.body}
          </Typography>
        )}
        {comment.media_urls?.length > 0 && (
          <Box sx={{ mt: 0.75 }}>
            <PostMediaGallery data={{ media_urls: comment.media_urls }} />
          </Box>
        )}
      </Box>
    </Stack>
  );
}

function ReactionBar({ post }) {
  const [react] = useAddTimelineReactionMutation();
  const [unReact] = useRemoveTimelineReactionMutation();
  const [anchorEl, setAnchorEl] = useState(null);

  const reactionMap = {};
  (post.reactions || []).forEach((r) => { reactionMap[r.emoji] = r.count; });
  const myReactions = post.my_reactions || [];
  const topReactions = (post.reactions || [])
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  const handleReact = async (emoji) => {
    try {
      if (myReactions.includes(emoji)) {
        await unReact({ postId: post.id, emoji }).unwrap();
      } else {
        await react({ postId: post.id, emoji }).unwrap();
      }
    } catch {
      toast.error("Could not update reaction");
    }
    setAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      {topReactions.map((r) => (
        <Button
          key={r.emoji}
          size="small"
          variant={myReactions.includes(r.emoji) ? "contained" : "outlined"}
          onClick={() => handleReact(r.emoji)}
          sx={{
            minWidth: "auto",
            px: 1,
            py: 0.25,
            borderRadius: 5,
            fontSize: "0.8rem",
            lineHeight: 1.5,
            color: myReactions.includes(r.emoji) ? undefined : "text.primary",
          }}
        >
          {r.emoji} {r.count > 0 ? r.count : ""}
        </Button>
      ))}

      <Tooltip title="Add reaction">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ ml: 0.5 }}
        >
          <AddReactionOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { p: 1, borderRadius: 2 } }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 220 }}>
          {QUICK_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleReact(emoji)}
              sx={{
                fontSize: "1.25rem",
                borderRadius: 1,
                border: myReactions.includes(emoji) ? "2px solid" : "2px solid transparent",
                borderColor: myReactions.includes(emoji) ? "primary.main" : "transparent",
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Stack>
  );
}

export default function PostCard({ post }) {
  const currentUser = useSelector(selectCurrentUser);
  const [addComment, { isLoading: commenting }] = useAddTimelineCommentMutation();
  const [uploadFile] = useUploadFileMutation();
  const [commentText, setCommentText] = useState("");
  const [commentImages, setCommentImages] = useState([]); // { file, preview, url }
  const [commentUploading, setCommentUploading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const commentImageRef = useRef(null);

  const MAX_COMMENT_IMAGES = 4;

  const PREVIEW_CHARS = 280;
  const needsTruncate = post.body?.length > PREVIEW_CHARS;
  const displayBody =
    needsTruncate && !expanded
      ? post.body.slice(0, PREVIEW_CHARS) + "…"
      : post.body;

  const submitComment = async () => {
    if (!commentText.trim() && commentImages.length === 0) return;

    let mediaUrls = [];
    if (commentImages.length > 0) {
      setCommentUploading(true);
      try {
        for (const img of commentImages) {
          const formData = new FormData();
          formData.append("file", img.file);
          const res = await uploadFile(formData).unwrap();
          if (res?.url) mediaUrls.push(res.url);
        }
      } catch {
        toast.error("Image upload failed. Please try again.");
        setCommentUploading(false);
        return;
      }
      setCommentUploading(false);
    }

    try {
      await addComment({ postId: post.id, body: commentText, mediaUrls }).unwrap();
      setCommentText("");
      setCommentImages([]);
    } catch (err) {
      toast.error(err?.data?.message || "Could not add comment");
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.07)" },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <UserAvatar
              src={post.author?.photo}
              name={post.author?.name || "School"}
              sx={{ width: 42, height: 42, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography fontWeight={700} variant="body2">
                  {post.author?.name || "School"}
                </Typography>
                {post.author?.roles?.length > 0 && (
                  <Chip
                    size="small"
                    label={post.author.roles[0]}
                    sx={{ height: 18, fontSize: "0.65rem", textTransform: "capitalize" }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {audienceLabel(post)}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  ·
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {timeAgo(post.created_at)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* Event date banner */}
          {post.event_date && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                py: 0.6,
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: "primary.200",
                borderRadius: 2,
                alignSelf: "flex-start",
              }}
            >
              <EventOutlinedIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
              <Typography variant="caption" fontWeight={700} color="primary.main">
                Event · {new Date(post.event_date + "T00:00:00").toLocaleDateString(undefined, {
                  weekday: "short", day: "numeric", month: "short", year: "numeric",
                })}
              </Typography>
            </Box>
          )}

          {/* Body */}
          {post.body && (
            <Box>
              <Typography
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.65 }}
              >
                {displayBody}
              </Typography>
              {needsTruncate && (
                <Button
                  size="small"
                  onClick={() => setExpanded((e) => !e)}
                  sx={{ textTransform: "none", p: 0, mt: 0.5, fontWeight: 600 }}
                >
                  {expanded ? "Show less" : "See more"}
                </Button>
              )}
            </Box>
          )}

          {/* Media */}
          <PostMediaGallery data={post.data} />

          {/* Reactions */}
          <ReactionBar post={post} />

          {/* Comment toggle */}
          <Button
            size="small"
            startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
            onClick={() => setShowComments((s) => !s)}
            sx={{
              textTransform: "none",
              alignSelf: "flex-start",
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            {post.comment_count > 0 ? `${post.comment_count} comment${post.comment_count > 1 ? "s" : ""}` : "Comment"}
          </Button>
        </Stack>
      </Box>

      {/* Comments section */}
      <Collapse in={showComments}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, bgcolor: "background.default" }}>
          <Stack spacing={1.5}>
            {(post.comments || []).map((c) => (
              <CommentItem key={c.id} comment={c} />
            ))}
            {post.comment_count > (post.comments?.length || 0) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ pl: 4.5 }}
              >
                {post.comment_count - post.comments.length} more comment(s) — open full view to see all
              </Typography>
            )}

            {/* Compose comment */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <UserAvatar
                src={currentUser?.photo || currentUser?.profile?.photo}
                name={`${currentUser?.profile?.first_name || currentUser?.first_name || ""} ${currentUser?.profile?.last_name || currentUser?.last_name || ""}`.trim() || currentUser?.username}
                sx={{ width: 28, height: 28, flexShrink: 0, mt: 0.5 }}
              />
              <Stack spacing={0.75} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Write a comment…"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        submitComment();
                      }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  />
                  <Tooltip title="Add image">
                    <span>
                      <IconButton
                        size="small"
                        disabled={commentImages.length >= MAX_COMMENT_IMAGES}
                        onClick={() => commentImageRef.current?.click()}
                        sx={{ alignSelf: "flex-end" }}
                      >
                        <ImageOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={commentUploading || commenting || (!commentText.trim() && commentImages.length === 0)}
                    onClick={submitComment}
                    sx={{ borderRadius: 3, alignSelf: "flex-end", textTransform: "none" }}
                  >
                    Reply
                  </Button>
                </Stack>

                {/* Image previews */}
                {commentImages.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {commentImages.map((img, i) => (
                      <Box
                        key={i}
                        sx={{ position: "relative", width: 56, height: 56, borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider" }}
                      >
                        <img
                          src={img.preview}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            URL.revokeObjectURL(img.preview);
                            setCommentImages((prev) => prev.filter((_, j) => j !== i));
                          }}
                          sx={{ position: "absolute", top: 0, right: 0, p: 0.25, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {commentUploading && <LinearProgress sx={{ borderRadius: 1 }} />}

                <input
                  ref={commentImageRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const remaining = MAX_COMMENT_IMAGES - commentImages.length;
                    const selected = files.slice(0, remaining).map((file) => ({
                      file,
                      preview: URL.createObjectURL(file),
                    }));
                    setCommentImages((prev) => [...prev, ...selected]);
                    e.target.value = "";
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
